import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import Hero from '../../widgets/Hero';
import * as css from './JavascriptDevelopment.m.css';
import Summary from '../../widgets/Summary';
import ConnectStrip from '../../widgets/ConnectStrip';
import FAQ from '../../widgets/FAQ';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Text from '../../widgets/Text';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';
import WhySitePen from '../../widgets/WhySitePen';

const angularLogo = require('../../assets/images/logo/small/angular.svg');
const reactLogo = require('../../assets/images/logo/small/react.svg');
const nodeLogo = require('../../assets/images/logo/small/node.svg');
const customSoftwareImg = require('../../assets/images/icon-illustration/custom-software.svg');
const serverSideImg = require('../../assets/images/icon-illustration/server-side-application.svg');
const backEndImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const engineeringImg = require('../../assets/images/illustration/engineering.svg');

const factory = create();

export const JavaScriptDevelopment = factory(function JavaScriptDevelopment() {
	return (
		<div classes={css.root}>
			<Head
				title="JavaScript Development Services Company | SitePen"
				description="SitePen is an elite JavaScript development company providing JavaScript development services with expertise in a wide range of framework technologies."
			/>

			<Hero background="level3Green">
				{{
					xlTitle: (
						<div>
							JavaScript
							<br />
							Development Services
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<div classes={[css.summaryHeader]}>
						<Text type="section">JavaScript Services</Text>
						<Text type="body">
							JavaScript is no longer confined to web browsers, and neither are we.
							Our deep and extensive expertise allows us to produce exceptional
							results where ever JavaScript runs.
						</Text>
					</div>
					<Summary trailingBias="medium" smaller paddingBottom="md">
						{{
							blurb: `JavaScript began its life in the browser, and that is still where it is most commonly found. We started SitePen with the mission of pushing the limits of what JavaScript could do in this, then novel, environment, and haven't stopped since. Our success depended upon our ability to learn the nuances of each browser's JavaScript API and how to work with them to deliver a unified experience. This focus on the fundamentals allows us to push modern browsers and frameworks to their limits. We don't take frameworks and their documentation at face value - we dig deeply into the underlying code to fully understand how the frameworks work. By thoroughly understanding the technologies, we can quickly and efficiently deliver solutions that perform exceptionally well while also being scalable and maintainable.`,
							title: `JavaScript Web Development`,
							image: <Image src={customSoftwareImg} size="sm" alt="" lazy />
						}}
					</Summary>
					<Summary trailingBias="medium" reverse smaller paddingBottom="md">
						{{
							blurb: `Node.js allowed JavaScript to break out of the browser and start running on servers as well. While the language remains the same, Node.js development is very different. Our engineers are dedicated to learning and often contributing to the most common server-side JavaScript libraries and frameworks. We take pride in our track record of delivering full-stack applications that integrate seamlessly and are well supported by suites of automated tests.`,
							title: `Server-Side Applications`,
							image: <Image src={serverSideImg} size="sm" alt="" lazy />
						}}
					</Summary>
					<Summary trailingBias="medium" smaller>
						{{
							blurb: `JavaScript has earned its reputation as the most commonly executed language in the world. Billions of lines of code run every day in client web browsers, and Node.js powered servers. In 2013, the Electron project allowed JavaScript to conquer another frontier - native applications. Unlike server-side JavaScript, these native apps handled everything from user interactions to database management. In response to this new revolution, our team did what it has always done - we followed JavaScript into this new world and have mastered the unique challenges that native app development brings. In many cases, we can use these tools to create applications with the ideal setup - a common language the works on client browsers, web servers, and users' mobile devices.`,
							title: `JavaScript App Development`,
							image: <Image src={backEndImg} size="sm" alt="" lazy />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							title: 'Why Choose SitePen as Your JavaScript Development Company',
							blurb: (
								<virtual>
									<p>
										SitePen was founded on the idea that JavaScript was going to
										change how we interact with web applications. While that
										sounds obvious now, things weren't quite as clear when we
										started this journey 20 years ago when Netscape Navigator
										and Internet Explorer dominated the browser market. We
										immediately started pushing the limits of what JavaScript
										could do and haven't stopped experimenting since.
									</p>
									<p>
										Over time, JavaScript has grown. The language has become
										more powerful and flexible, and the ecosystem has expanded
										due to projects such as Node.js and TypeScript. Today,
										JavaScript has proven itself as one of the only languages
										that can successfully deliver every part of an enterprise
										application from the data layer to the user interface.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<ColumnSummary>
						{{
							title: 'JavaScript Development Frameworks',
							blurb: (
								<virtual>
									It is almost impossible to learn every JavaScript framework.
									While we do try to keep an eye on the broader landscape, we
									focus our efforts on mastering a few frameworks that are ideal
									tools for creating enterprise-grade applications.
								</virtual>
							),
							items: [
								{
									image: (
										<img
											src={angularLogo}
											loading="lazy"
											classes={[css.logo, css.angularLogo]}
											alt="Angular"
										/>
									),
									title: 'Angular Development',
									blurb: (
										<virtual>
											When introduced in 2010, AngularJS accomplished what no
											other framework had; it established itself as a default
											solution for enterprise development. Today, the Angular
											project continues to provide a comprehensive set of
											tools and best practices to support the creation of
											world-class applications. Angular, however, is a complex
											project and requires extensive knowledge and experience
											to unlock its benefits. To maximize our effectiveness,
											we have cultivated a relationship with the Angular core
											team. This relationship allows us to build Angular
											applications that take advantage of today's best
											practices while keeping the door open to adopting future
											enhancements as the framework evolves.
										</virtual>
									)
								},
								{
									image: (
										<img
											src={reactLogo}
											loading="lazy"
											classes={[css.logo, css.reactLogo]}
											alt="React"
										/>
									),
									title: 'React Development',
									blurb: (
										<virtual>
											React is a fast, lightweight user-interface library that
											can deliver fantastic performance without the overhead
											of a complicated application framework. When combined
											with a data-management library, such as Redux, React
											gains much of the capabilities of an extensive framework
											while allowing developers the freedom to organize the
											project without the strictures that a framework brings.
											Our team has successfully deployed projects of all sizes
											using the React ecosystem, including React Native - an
											implementation that allows JavaScript applications to
											run as native applications on desktop and mobile
											platforms.
										</virtual>
									)
								},
								{
									image: (
										<img
											src={nodeLogo}
											loading="lazy"
											classes={[css.logo, css.nodeLogo]}
											alt="Node.js"
										/>
									),
									title: 'Node.js Development',
									blurb: (
										<virtual>
											To effectively use Node.js, developers need to master
											its non-blocking IO model. As a single-threaded
											language, JavaScript applications can be crippled by
											long-running tasks that block the main process. Our team
											leverages decades of experience to write asynchronous
											code that is not only functional but readable as well.
											This critical skill allows us to develop and enhance
											applications as requirements are refined, and new
											use-cases are discovered.
										</virtual>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							title: 'Our JavaScript Development Process',
							image: <Image src={engineeringImg} size="lg" />,
							blurb: (
								<virtual>
									Our development partnership with you starts the moment you reach
									out to us. Our solution architects will work with you to
									understand your goals and gather the background information that
									informs our entire development process. Our project management,
									senior designers, and technical leads will take this information
									and develop high-level project plans before you even see a
									contract. We will then work with you to ensure that we agree
									upon a set of clear, achievable goals. Throughout the
									development process, we stay in constant contact with you. As we
									all learn more about the project, we'll find areas that need to
									be refined and changed. We will work with you to integrate these
									changes into the plan so that the software reflects all of the
									lessons we learned during its development.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise />
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="JavaScript Development FAQs">
					{[
						{
							title: 'What Are The Benefits Of JavaScript Development?',
							content: (
								<p>
									The JavaScript language is one of the only languages that has
									achieved the long-pursued goal of executing on any platform.
									This unique trait allows a team of developers to master a single
									language while maintaining the ability to focus that mastery on
									every aspect of an application. Additionally, the aggressive
									optimizations that have been made to each platform's JavaScript
									runtime allow applications that run at near-native speeds.
								</p>
							)
						},
						{
							title: 'Why Hire SitePen JavaScript Developers?',
							content: (
								<p>
									SitePen was founded on the premise that JavaScript would become
									the most important language of our time. Today, JavaScript is
									the most commonly used language in software projects and our
									decades of experience allow us to maximize the impact of every
									line of code. Whether your project involves refreshing a legacy
									jQuery-based application or developing a cutting edge mobile
									application using React Native, our engineers have the knowledge
									and experience to exceed your expectations.
								</p>
							)
						},
						{
							title: 'Which JavaScript Framework is Best?',
							content: (
								<p>
									Every application has a unique blend of opportunities and
									constraints. Similarly, every JavaScript framework is optimized
									to best solve a specific set of problems. For applications with
									a complex set of business rules and large development teams, we
									often find that the strong opinions of the Angular framework are
									a good fit. For applications with smaller teams or less rigorous
									business rules, React's flexibility will often deliver superior
									results. These, however, are very general guidelines. Our team
									will perform a detailed analysis of your requirements to
									recommend the ideal mix of libraries and frameworks to balance
									your software system's speed, accuracy, and reliability.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default JavaScriptDevelopment;
